/**
 * MediaQualityToggle — 3-segment switcher for inspection photo upload quality.
 *
 * Apr 2026: Per-inspection tier (Low / Medium / High) for photos uploaded by
 * the mechanic app. The mechanic app reads `inspections.media_quality_tier`
 * before each upload and compresses the image to the matching (MAX_DIM, q)
 * settings. See `mechanic-app-native/src/lib/imageCompressor.ts`.
 *
 * UX policy (per product owner, Apr 2026 review):
 *   • Anyone logged in can change the tier — no role gate, no terminal lock.
 *   • Even on a COMPLETED / PUBLISHED inspection, the tier flag stays editable
 *     for re-inspections / manual reupload flows.
 *   • Toast is explicit that the change applies to the NEXT mechanic upload.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TIERS = [
  {
    key: 'low',
    label: 'Low',
    description: '1920 px · q70 · ~250 KB / photo · cheapest storage (default)',
    activeBg: 'bg-emerald-500',
  },
  {
    key: 'medium',
    label: 'Medium',
    description: '2560 px · q85 · ~700 KB / photo · clearer condition shots',
    activeBg: 'bg-amber-500',
  },
  {
    key: 'high',
    label: 'High',
    description: '4096 px · q95 · ~2 MB / photo · disputes / B2B premium / luxury',
    activeBg: 'bg-rose-500',
  },
];

export default function MediaQualityToggle({ inspectionId }) {
  const [tier, setTier] = useState('low');
  const [updatedByEmail, setUpdatedByEmail] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchState = useCallback(async () => {
    if (!inspectionId) return;
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await fetch(
        `${apiUrl}/api/inspections/${inspectionId}/media-quality`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        setLoading(false);
        return;
      }
      const data = await r.json();
      setTier(data.tier || 'low');
      setUpdatedByEmail(data.updated_by_email || null);
      setUpdatedAt(data.updated_at || null);
    } catch {
      // Network error — keep defaults, don't break the modal
    } finally {
      setLoading(false);
    }
  }, [inspectionId, apiUrl]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const handleTierChange = async (newTier) => {
    if (newTier === tier || saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const r = await fetch(
        `${apiUrl}/api/inspections/${inspectionId}/media-quality`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ tier: newTier }),
        },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error('Could not change media quality', {
          description: body.detail || `HTTP ${r.status}`,
        });
        return;
      }
      setTier(newTier);
      const newLabel = TIERS.find((t) => t.key === newTier)?.label || newTier;
      toast.success(`Media quality set to ${newLabel}`, {
        description:
          'Applies to the next photo the mechanic uploads on this inspection. ' +
          'Existing photos are unchanged.',
        duration: 6000,
      });
      fetchState();
    } catch (e) {
      toast.error('Could not change media quality', { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center gap-2 bg-slate-900/60 ring-1 ring-white/20 rounded-lg px-2.5 py-1"
        data-testid="media-quality-toggle-loading"
      >
        <Loader2 className="h-3.5 w-3.5 text-white animate-spin" />
        <span className="text-[11px] text-blue-100">Media…</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 bg-slate-900/60 ring-1 ring-white/20 rounded-lg px-2.5 py-1 shadow-inner"
      data-testid="media-quality-toggle"
      title="Photo quality for FUTURE mechanic uploads on this inspection. Existing photos are unchanged."
    >
      <Camera className="h-3.5 w-3.5 text-white" />
      <span className="text-[11px] text-blue-50 font-semibold tracking-wide hidden sm:inline">
        MEDIA
      </span>
      <div
        className="flex rounded-md overflow-hidden ring-1 ring-white/30"
        data-testid="media-quality-segment-group"
      >
        {TIERS.map((t) => {
          const isActive = t.key === tier;
          return (
            <button
              key={t.key}
              type="button"
              disabled={saving}
              onClick={() => handleTierChange(t.key)}
              data-testid={`media-quality-tier-${t.key}`}
              data-active={isActive ? 'true' : 'false'}
              title={t.description}
              className={[
                'px-2.5 py-0.5 text-[11px] font-bold transition-all duration-150',
                isActive
                  ? `${t.activeBg} text-white shadow-md`
                  : 'bg-slate-800/40 text-slate-200 hover:bg-slate-700/60 hover:text-white',
                saving && !isActive ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {saving && <Loader2 className="h-3 w-3 text-white animate-spin" />}
      {updatedByEmail && !saving && (
        <span
          className="text-[10px] text-blue-200 hidden lg:inline truncate max-w-[140px]"
          title={`Last changed by ${updatedByEmail}${updatedAt ? ' at ' + new Date(updatedAt).toLocaleString() : ''}`}
        >
          · {updatedByEmail.split('@')[0]}
        </span>
      )}
    </div>
  );
}
